/**
 * Fidget Finder — Tinder-style product discovery
 * Vanilla JS, no dependencies beyond Bootstrap (already loaded by theme).
 */

(function () {
  'use strict';

  // ── Personality map ──────────────────────────────────────────────────────
  // Set via metafield: custom.fidget_categories (list type)
  // Tier 1 (fidget mechanic): fidget-toy
  // Tier 2 (theme/character): dinosaur | dragon | animals | cat | dog | bug |
  //                            ocean | halloween | keyring | art-toy |
  //                            valentines | christmas | easter
  var PERSONALITIES = {
    // ── Tier 1: Fidget mechanic ──
    'fidget-toy':      { emoji: '🌀', label: 'The Fidget Fanatic',       desc: 'You need something in your hands at all times. The more satisfying the motion, the better.' },

    // ── Tier 2: Theme / character ──
    'dinosaur':        { emoji: '🦕', label: 'The Dino Devotee',         desc: 'Prehistoric and proud. Your desk is basically a Jurassic Park and you wouldn\'t have it any other way.' },
    'dragon':          { emoji: '🐉', label: 'The Dragon Hoarder',       desc: 'Chonk or classic, you don\'t discriminate. Your collection has teeth and you love it.' },
    'animals':         { emoji: '🐾', label: 'The Animal Lover',         desc: 'Cute, characterful and full of personality — just like you. Every wiggle needs a home on your shelf.' },
    'cute':            { emoji: '🥰', label: 'The Softie',               desc: 'You\'re here for the adorable ones. If it\'s round, smiley and squeezable, it\'s already in your cart.' },
    'cat':             { emoji: '🐱', label: 'The Cat Person',           desc: 'Independent, a little mysterious, and obsessed with anything small and fidgetable. Classic.' },
    'dog':             { emoji: '🐶', label: 'The Dog Lover',            desc: 'Loyal, enthusiastic and always excited about new things. Your wiggle collection grows fast.' },
    'bug':             { emoji: '🐛', label: 'The Bug Enthusiast',       desc: 'Most people walk past bugs. You want to squeeze them. The creepier the cuter, that\'s your motto.' },
    'ocean':           { emoji: '🌊', label: 'The Ocean Dreamer',        desc: 'Calm on the surface, lots going on underneath. Sea creatures are your spirit wiggle.' },
    'halloween':       { emoji: '💀', label: 'The Spooky One',           desc: 'Dark, cute and gloriously weird. Halloween isn\'t a season for you — it\'s a lifestyle.' },
    'keyring':         { emoji: '🔑', label: 'The On-the-Go Wrangler',   desc: 'You fidget everywhere — in the car, in the queue, in the meeting. Portability is non-negotiable.' },

    'art-toy':         { emoji: '🎨', label: 'The Art Toy Collector',    desc: 'You appreciate craft, character and detail. Your shelves are curated, not just filled.' },
    'valentines':      { emoji: '💕', label: 'The Romantic',             desc: 'Heart-first and love-led. You collect with feeling and gift with intention.' },
    'christmas':       { emoji: '🎄', label: 'The Festive One',          desc: 'Every season is an excuse to add to the collection. You celebrate year-round and love it.' },
    'easter':          { emoji: '🐣', label: 'The Easter Enthusiast',    desc: 'Pastel, fluffy, and full of seasonal joy. You take Easter very seriously and the collection proves it.' }
  };

  var FALLBACK_PERSONALITIES = [
    { emoji: '✨', label: 'The Explorer',       desc: 'You keep an open mind — the whole range is fair game. Your next favourite wiggle is always just around the corner.' },
    { emoji: '🎯', label: 'The Collector',      desc: 'One is never enough. You\'re building something special, one wiggle at a time.' },
    { emoji: '🌈', label: 'The Free Spirit',    desc: 'Rules? What rules? You pick on vibes alone and your collection is gloriously unpredictable.' },
    { emoji: '🧩', label: 'The Curator',        desc: 'Thoughtful, intentional, and impossibly picky in the best way. Every wiggle earns its place.' }
  ];

  var DEFAULT_PERSONALITY = FALLBACK_PERSONALITIES[0];

  // Minimum drag distance (px) to count as a swipe
  var SWIPE_THRESHOLD = 90;

  // Alternating rotation per depth level — creates a real "fanned deck" feel
  // depth 0 = top (no rotation), 1 = +3deg, 2 = -3deg
  var CARD_ROTATIONS = ['0deg', '3deg', '-3deg'];

  // Keep the quiz short so people actually reach the payoff.
  var MAX_DECK_SIZE          = 20; // cap cards per round (sampled from the collection)
  var MIN_SWIPES_FOR_RESULTS = 6;  // reveal "show my matches" after this many swipes
  var MAX_RECOMMENDATIONS    = 12; // unseen products to surface in results

  // Track initialised sections to prevent duplicate listeners
  var initializedSections = {};

  // ── Init ─────────────────────────────────────────────────────────────────

  function init() {
    document.querySelectorAll('[id^="fidget-finder-"]').forEach(function (section) {
      var sectionId = section.dataset.sectionId;
      if (sectionId) initFinder(sectionId);
    });
  }

  function initFinder(sectionId) {
    // Guard against duplicate initialisation
    if (initializedSections[sectionId]) return;
    initializedSections[sectionId] = true;

    // Elements
    var finderEl         = document.getElementById('fidget-finder-' + sectionId);
    var introEl          = document.getElementById('ff-intro-' + sectionId);
    var swipeEl          = document.getElementById('ff-swipe-' + sectionId);
    var resultsEl        = document.getElementById('ff-results-' + sectionId);
    var stackEl          = document.getElementById('ff-stack-' + sectionId);
    var progressEl       = document.getElementById('ff-progress-' + sectionId);
    var progressWrapEl   = document.getElementById('ff-progressbar-' + sectionId);
    var counterEl        = document.getElementById('ff-counter-' + sectionId);
    var startBtn         = document.getElementById('ff-start-' + sectionId);
    var giftBtn          = document.getElementById('ff-gift-' + sectionId);
    var giftBudgetEl     = document.getElementById('ff-gift-budget-' + sectionId);
    var nopeBtn          = document.getElementById('ff-nope-' + sectionId);
    var likeBtn          = document.getElementById('ff-like-' + sectionId);
    var personalityEmoji = document.getElementById('ff-personality-emoji-' + sectionId);
    var personalityEl    = document.getElementById('ff-personality-' + sectionId);
    var personalityTitle = document.getElementById('ff-personality-title-' + sectionId);
    var personalityDesc  = document.getElementById('ff-personality-desc-' + sectionId);
    var topMatchEl       = document.getElementById('ff-top-match-' + sectionId);
    var noLikesEl        = document.getElementById('ff-no-likes-' + sectionId);
    var resultsHeader    = document.getElementById('ff-results-header-' + sectionId);
    var resultsGrid      = document.getElementById('ff-results-grid-' + sectionId);
    var shareBtn         = document.getElementById('ff-share-' + sectionId);
    var restartBtn       = document.getElementById('ff-restart-' + sectionId);
    var earlyExitBtn     = document.getElementById('ff-early-exit-' + sectionId);
    var recsEl           = document.getElementById('ff-recs-' + sectionId);
    var recsTitleEl      = document.getElementById('ff-recs-title-' + sectionId);
    var recsSubEl        = document.getElementById('ff-recs-sub-' + sectionId);
    var recsGrid         = document.getElementById('ff-recs-grid-' + sectionId);
    var dataEl           = document.getElementById('ff-data-' + sectionId);

    if (!dataEl || !stackEl) return;

    var products;
    try {
      products = JSON.parse(dataEl.textContent);
    } catch (e) {
      console.warn('[FidgetFinder] Could not parse product data', e);
      return;
    }
    if (!products || !products.length) return;

    // ── Peek cards — four random products scattered behind the intro card ────
    function renderPeekCards() {
      var ffIntro = introEl && introEl.querySelector('.ff-intro');
      if (!ffIntro || products.length < 2) return;

      // Scatter random products behind the intro card. The inner ring (a–h)
      // shows on all screens; the outer ring (i–n) is CSS-gated to wide
      // viewports (≥1400px) where there's empty space to fill.
      var pool  = shuffle(products.slice());
      var sides = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n'];
      var INNER_RING = 8; // first 8 are always visible & eager-loaded
      var count = sides.length;
      // Repeat pool if fewer products than slots
      var picks = [];
      for (var pi = 0; pi < count; pi++) picks.push(pool[pi % pool.length]);

      // Wrap .ff-intro in a scene so peek cards can be positioned relative to it
      var scene = document.createElement('div');
      scene.className = 'ff-intro-scene';
      ffIntro.parentNode.insertBefore(scene, ffIntro);
      scene.appendChild(ffIntro);

      sides.forEach(function (side, i) {
        var card = document.createElement('div');
        card.className = 'ff-peek-card ff-peek-card--' + side;
        card.setAttribute('aria-hidden', 'true');

        var img = document.createElement('img');
        img.src = picks[i].image;
        img.alt = picks[i].title;
        // Outer ring is hidden on most screens — don't pay to load it eagerly.
        img.loading = (i < INNER_RING) ? 'eager' : 'lazy';
        img.draggable = false;

        card.appendChild(img);
        scene.appendChild(card);
      });
    }
    renderPeekCards();

    // State
    var deck              = products.slice(); // active filtered deck
    var currentIndex      = 0;
    var liked             = [];
    var seenIds           = {}; // ids of every card shown (liked OR passed) — powers "unseen" recs
    var isAnimating       = false; // Prevents race conditions on rapid clicks
    var currentPersonality = null; // Set when results are shown, used in ATC events
    var mode              = 'self'; // 'self' or 'gift' — gift reframes copy + filters by budget
    var budgetCents       = null;   // gift-mode price ceiling in cents, null = any

    // ── Screen management ─────────────────────────────────────────────────

    function showScreen(el) {
      [introEl, swipeEl, resultsEl].forEach(function (s) {
        if (s) s.classList.add('d-none');
      });
      if (el) el.classList.remove('d-none');
      if (finderEl) finderEl.classList.toggle('fidget-finder--results', el === resultsEl);
    }

    // ── Progress ──────────────────────────────────────────────────────────

    function updateProgress() {
      var total  = deck.length;
      var viewed = Math.min(currentIndex + 1, total);
      var pct    = Math.round((currentIndex / total) * 100);

      if (progressEl) progressEl.style.width = pct + '%';
      if (progressWrapEl) progressWrapEl.setAttribute('aria-valuenow', pct);
      if (counterEl) counterEl.textContent = viewed + ' of ' + total;
    }

    function completeProgress() {
      if (progressEl) progressEl.style.width = '100%';
      if (progressWrapEl) progressWrapEl.setAttribute('aria-valuenow', '100');
      if (counterEl) counterEl.textContent = deck.length + ' of ' + deck.length;
    }

    // Reveal the "show my matches" shortcut once we have enough signal.
    function maybeShowEarlyExit() {
      if (!earlyExitBtn) return;
      var swiped = Object.keys(seenIds).length;
      if (swiped >= MIN_SWIPES_FOR_RESULTS && currentIndex < deck.length) {
        earlyExitBtn.classList.remove('d-none');
      }
    }

    // ── Card creation ─────────────────────────────────────────────────────

    function createCard(product, depth) {
      var card = document.createElement('div');
      card.className = 'ff-card product-card' + (depth === 0 ? ' ff-card--top' : '');
      card.style.setProperty('--ff-depth', depth);
      card.style.setProperty('--ff-rotate', CARD_ROTATIONS[depth] || '0deg');
      card.setAttribute('aria-label', product.title);

      // Sale badge (top-left) — mirrors product-card.liquid badge logic
      var saleBadgeHTML = '';
      if (product.compare_price_cents && product.price_cents) {
        var discountPercentage = (product.compare_price_cents - product.price_cents) / product.compare_price_cents * 100;
        var pct = Math.round(discountPercentage);
        if (discountPercentage >= 5) saleBadgeHTML = '<span class="product-sale-badge badge">' + pct + '% Off</span>';
      }

      // New badge (top-right) — only if no sale badge
      var newBadgeHTML = '';
      if (!saleBadgeHTML && product.shopify_tags && product.shopify_tags.map(function (t) { return t.toLowerCase(); }).indexOf('new') > -1) {
        newBadgeHTML = '<span class="product-new-badge badge"> New! </span>';
      }

      // Price HTML — mirrors product-card-price.liquid
      var currency = product.currency || '';
      var hasRange = product.price_max_cents && product.price_max_cents !== product.price_cents;
      var priceHTML;
      if (hasRange) {
        // Price range — always show "From $min", sale badge covers the discount
        priceHTML =
          '<p class="product-card-price product-price mb-4">' +
            '<span class="product-card-price-final">' +
              '<span class="product-card-price-from-label">From </span>' +
              escapeHTML(product.price) + '&nbsp;<span class="price-currency">' + escapeHTML(currency) + '</span>' +
            '</span>' +
          '</p>';
      } else if (product.compare_price) {
        // Single price on sale
        priceHTML =
          '<p class="product-card-price product-price mb-4">' +
            '<s class="product-card-price-compare text-muted ms-2">' + escapeHTML(product.compare_price) + '</s>' +
            '<span class="product-card-price-final product-price-final-sale">' +
              escapeHTML(product.price) + '&nbsp;<span class="price-currency">' + escapeHTML(currency) + '</span>' +
            '</span>' +
          '</p>';
      } else {
        priceHTML =
          '<p class="product-card-price product-price mb-4">' +
            '<span class="product-card-price-final">' +
              escapeHTML(product.price) + '&nbsp;<span class="price-currency">' + escapeHTML(currency) + '</span>' +
            '</span>' +
          '</p>';
      }

      card.innerHTML =
        '<div class="product-card-img-wrapper position-relative" style="aspect-ratio:1/1;">' +
          '<img src="' + escapeAttr(product.image) + '" alt="' + escapeAttr(product.image_alt) + '" class="product-card-image w-100 h-100 object-fit-cover" loading="eager" draggable="false">' +
          saleBadgeHTML +
          newBadgeHTML +
        '</div>' +
        '<div class="product-card-body mt-3">' +
          '<h3 class="product-card-title h5 mb-2">' + escapeHTML(product.title) + '</h3>' +
          priceHTML +
        '</div>' +
        '<div class="ff-card__tint" aria-hidden="true"></div>' +
        '<div class="ff-card__overlay ff-card__overlay--like" aria-hidden="true">♥ YES!</div>' +
        '<div class="ff-card__overlay ff-card__overlay--nope" aria-hidden="true">✕ NOPE</div>';

      return card;
    }

    // ── Image preloading ──────────────────────────────────────────────────
    // Kick off browser fetches for images that aren't in the DOM yet,
    // so they're cached by the time a card needs them.

    var preloadCache = {};

    function preloadUpcoming() {
      var from = currentIndex + 3;
      var to   = Math.min(from + 2, deck.length);
      for (var i = from; i < to; i++) {
        var src = deck[i] && deck[i].image;
        if (src && !preloadCache[src]) {
          preloadCache[src] = true;
          var img = new Image();
          img.src = src;
        }
      }
    }

    // ── Render card stack (top 3 cards) ───────────────────────────────────

    function renderStack() {
      stackEl.innerHTML = '';
      var remaining = deck.length - currentIndex;
      var visible   = Math.min(3, remaining);
      // Insert back-to-front so the top card (depth 0) is last in DOM but highest z-index
      for (var i = visible - 1; i >= 0; i--) {
        var card = createCard(deck[currentIndex + i], i);
        stackEl.appendChild(card);
      }
      attachDrag(getTopCard());
      preloadUpcoming();
    }

    function getTopCard() {
      return stackEl.querySelector('.ff-card--top');
    }

    // ── Drag / swipe ──────────────────────────────────────────────────────

    var dragState = {
      active:   false,
      startX:   0,
      startY:   0,
      currentX: 0,
      card:     null
    };

    function attachDrag(card) {
      if (!card) return;
      card.addEventListener('touchstart', onPointerDown, { passive: true });
      card.addEventListener('mousedown',  onPointerDown);
    }

    function onPointerDown(e) {
      if (isAnimating) return;
      if (e.type === 'mousedown' && e.button !== 0) return;

      var touch = e.touches ? e.touches[0] : e;
      dragState.active   = true;
      dragState.startX   = touch.clientX;
      dragState.startY   = touch.clientY;
      dragState.currentX = 0;
      dragState.card     = getTopCard();

      if (e.type === 'mousedown') {
        document.addEventListener('mousemove', onPointerMove);
        document.addEventListener('mouseup',   onPointerUp);
      } else {
        // passive:false so we can preventDefault during horizontal drags on iOS
        document.addEventListener('touchmove', onPointerMove, { passive: false });
        document.addEventListener('touchend',  onPointerUp);
      }
    }

    function onPointerMove(e) {
      if (!dragState.active || !dragState.card) return;

      var touch = e.touches ? e.touches[0] : e;
      var dx    = touch.clientX - dragState.startX;
      var dy    = touch.clientY - dragState.startY;
      dragState.currentX = dx;

      // Block page scroll when user is clearly dragging horizontally
      if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
        if (e.cancelable) e.preventDefault();
      }

      var card   = dragState.card;
      var rotate = dx * 0.08;

      card.style.transition = 'none';
      card.style.transform  = 'translateX(' + dx + 'px) rotate(' + rotate + 'deg)';

      var likeOverlay = card.querySelector('.ff-card__overlay--like');
      var nopeOverlay = card.querySelector('.ff-card__overlay--nope');
      var tint        = card.querySelector('.ff-card__tint');
      var ratio = Math.min(Math.abs(dx) / 80, 1);

      if (dx > 20) {
        likeOverlay.style.opacity = ratio;
        nopeOverlay.style.opacity = 0;
        if (tint) tint.style.backgroundColor = 'rgba(34, 197, 94, ' + (ratio * 0.25) + ')';
        likeBtn.classList.add('ff-action-btn--active');
        nopeBtn.classList.remove('ff-action-btn--active');
      } else if (dx < -20) {
        nopeOverlay.style.opacity = ratio;
        likeOverlay.style.opacity = 0;
        if (tint) tint.style.backgroundColor = 'rgba(239, 68, 68, ' + (ratio * 0.25) + ')';
        nopeBtn.classList.add('ff-action-btn--active');
        likeBtn.classList.remove('ff-action-btn--active');
      } else {
        likeOverlay.style.opacity = 0;
        nopeOverlay.style.opacity = 0;
        if (tint) tint.style.backgroundColor = '';
        likeBtn.classList.remove('ff-action-btn--active');
        nopeBtn.classList.remove('ff-action-btn--active');
      }
    }

    function onPointerUp() {
      if (!dragState.active) return;
      dragState.active = false;
      cleanupDocListeners();

      likeBtn.classList.remove('ff-action-btn--active');
      nopeBtn.classList.remove('ff-action-btn--active');

      var card  = dragState.card;
      var lastDx = dragState.currentX; // capture before reset
      dragState.currentX = 0;
      dragState.card     = null;

      if (!card) return;

      if (Math.abs(lastDx) >= SWIPE_THRESHOLD) {
        animateFlyOut(card, lastDx > 0);
      } else {
        snapBack(card);
      }
    }

    function cleanupDocListeners() {
      document.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('mouseup',   onPointerUp);
      document.removeEventListener('touchmove', onPointerMove);
      document.removeEventListener('touchend',  onPointerUp);
    }

    // ── Emoji burst ───────────────────────────────────────────────────────

    function burstEmojis(card, isLike) {
      var emojis  = isLike ? ['❤️','💖','💗','💝','💓'] : ['🗑️','💀','👎','😬','🚫'];
      var count   = 10;
      var rect    = card.getBoundingClientRect();
      // Origin: centre of card — use viewport coords + position:fixed
      var ox = rect.left + rect.width  / 2;
      var oy = rect.top  + rect.height / 2;

      for (var i = 0; i < count; i++) {
        (function (idx) {
          var el = document.createElement('span');
          el.textContent = emojis[idx % emojis.length];
          el.style.cssText = [
            'position:fixed',
            'left:' + ox + 'px',
            'top:'  + oy + 'px',
            'font-size:' + (1.4 + Math.random() * 1.2) + 'rem',
            'pointer-events:none',
            'user-select:none',
            'z-index:9999',
            'opacity:0',
            'transform:translate(-50%,-50%)',
            'will-change:transform,opacity'
          ].join(';');
          document.body.appendChild(el);

          // Random trajectory — strong sideways bias, gentle vertical
          var spread   = 100;
          var bias     = isLike ? 200 : -200;
          var vx       = bias + (Math.random() - 0.5) * spread;
          var vy       = -(20 + Math.random() * 80);   // mostly sideways, slight upward
          var rot      = (Math.random() - 0.5) * 720;
          var duration = 700 + Math.random() * 400;
          var delay    = idx * 40;

          var start = null;
          function step(ts) {
            if (!start) start = ts + delay;
            var elapsed = ts - start;
            if (elapsed < 0) { requestAnimationFrame(step); return; }
            var t = Math.min(elapsed / duration, 1);
            // Ease out cubic
            var e = 1 - Math.pow(1 - t, 3);
            var gravity = 200 * t * t;
            var x = ox + vx * e;
            var y = oy + vy * e + gravity;
            var opacity = t < 0.12 ? (t / 0.12) : (t < 0.6 ? 1 : 1 - ((t - 0.6) / 0.4));
            var scale   = 0.5 + e * 0.8;
            el.style.left    = x + 'px';
            el.style.top     = y + 'px';
            el.style.opacity = opacity;
            el.style.transform = 'translate(-50%,-50%) rotate(' + (rot * e) + 'deg) scale(' + scale + ')';
            if (t < 1) {
              requestAnimationFrame(step);
            } else {
              el.remove();
            }
          }
          requestAnimationFrame(step);
        })(i);
      }
    }

    // ── Card animations ───────────────────────────────────────────────────

    function animateFlyOut(card, isLike) {
      if (isAnimating) return;
      isAnimating = true;

      burstEmojis(card, isLike);

      var dir = isLike ? 1 : -1;
      card.classList.remove('ff-card--top');

      var overlay = card.querySelector(isLike ? '.ff-card__overlay--like' : '.ff-card__overlay--nope');
      if (overlay) overlay.style.opacity = '1';

      card.style.transition = 'transform 0.42s ease, opacity 0.42s ease';
      card.style.transform  = 'translateX(' + (dir * 130) + '%) rotate(' + (dir * 22) + 'deg)';
      card.style.opacity    = '0';

      var swiped = deck[currentIndex];
      if (swiped) seenIds[swiped.id] = true;
      if (isLike && swiped) {
        liked.push(swiped);
      }

      trackEvent(isLike ? 'QuizLike' : 'QuizPass', {
        product_id:    deck[currentIndex] ? String(deck[currentIndex].id) : '',
        product_title: deck[currentIndex] ? deck[currentIndex].title : '',
        product_index: currentIndex + 1,
        deck_size:     deck.length
      });

      // Timeout fallback in case transitionend never fires (tab backgrounded, etc.)
      var done = false;
      function onDone() {
        if (done) return;
        done = true;
        clearTimeout(fallbackTimer);
        card.remove();
        isAnimating = false;
        advance();
      }

      var fallbackTimer = setTimeout(onDone, 550);
      card.addEventListener('transitionend', onDone, { once: true });
    }

    function snapBack(card) {
      var likeOverlay = card.querySelector('.ff-card__overlay--like');
      var nopeOverlay = card.querySelector('.ff-card__overlay--nope');
      if (likeOverlay) likeOverlay.style.opacity = '0';
      if (nopeOverlay) nopeOverlay.style.opacity = '0';
      var tintEl = card.querySelector('.ff-card__tint');
      if (tintEl) tintEl.style.backgroundColor = '';

      card.style.transition = 'transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)';
      card.style.transform  = '';
    }

    // ── Advance to next card ──────────────────────────────────────────────

    function advance() {
      currentIndex++;

      if (currentIndex >= deck.length) {
        completeProgress();
        setTimeout(showResults, 350);
        return;
      }

      updateProgress();
      preloadUpcoming();
      maybeShowEarlyExit();

      // Promote remaining cards: each moves one step closer to the front
      var cards = Array.from(stackEl.querySelectorAll('.ff-card'));
      cards.forEach(function (c) {
        var depth    = parseInt(c.style.getPropertyValue('--ff-depth') || '1', 10);
        var newDepth = depth - 1;
        c.style.setProperty('--ff-depth', newDepth);
        c.style.setProperty('--ff-rotate', CARD_ROTATIONS[newDepth] || '0deg');
        c.style.transition = 'transform 0.3s ease';
        if (newDepth === 0) {
          c.classList.add('ff-card--top');
          attachDrag(c);
        }
      });

      // Add new back card if more products remain
      var newBackIndex = currentIndex + 2;
      if (newBackIndex < deck.length) {
        var newCard = createCard(deck[newBackIndex], 2);
        stackEl.insertBefore(newCard, stackEl.firstChild);
      }
    }

    // ── Personality result ────────────────────────────────────────────────

    function getPersonality() {
      if (!liked.length) return DEFAULT_PERSONALITY;

      var counts = {};
      liked.forEach(function (p) {
        (p.tags || []).forEach(function (tag) {
          var t = tag.toLowerCase();
          if (PERSONALITIES[t]) counts[t] = (counts[t] || 0) + 1;
        });
      });

      if (!Object.keys(counts).length) {
        return FALLBACK_PERSONALITIES[Math.floor(Math.random() * FALLBACK_PERSONALITIES.length)];
      }

      // Priority rules: when two tags tie, prefer the more specific one
      var PRIORITY = { 'ocean': ['animals'], 'halloween': ['art-toy'] };

      var topTag = Object.keys(counts).sort(function (a, b) {
        var diff = counts[b] - counts[a];
        if (diff !== 0) return diff;
        // Tied — check if one overrides the other
        for (var winner in PRIORITY) {
          var losers = PRIORITY[winner];
          if (a === winner && losers.indexOf(b) > -1) return -1;
          if (b === winner && losers.indexOf(a) > -1) return 1;
        }
        return 0;
      })[0];

      return PERSONALITIES[topTag];
    }

    // ── Top matches — the liked products most central to the shopper's taste ─
    // Ranks every liked product by how strongly its tags overlap with the rest
    // of the likes (so the most "on-theme" picks rise), with a light
    // social-proof tiebreaker. Returns up to n products, best first.

    function getTopMatches(n) {
      n = n || 3;
      if (!liked.length) return [];

      // Tag frequency across all liked products
      var counts = {};
      liked.forEach(function (p) {
        (p.tags || []).forEach(function (tag) {
          var t = String(tag).toLowerCase();
          counts[t] = (counts[t] || 0) + 1;
        });
      });

      function score(p) {
        var s = 0;
        (p.tags || []).forEach(function (tag) {
          var t = String(tag).toLowerCase();
          if (counts[t]) s += counts[t];
        });
        // Social-proof tiebreaker — never outweighs taste overlap
        var r  = parseFloat(p.rating) || 0;
        var rc = parseInt(p.review_count, 10) || 0;
        s += (r * 2 + Math.min(Math.log(rc + 1), 3) * 0.5) * 0.1;
        return s;
      }

      return liked.slice()
        .sort(function (a, b) { return score(b) - score(a); })
        .slice(0, n);
    }

    // ── Recommendations — products the shopper NEVER saw ───────────────────
    // The core "smart" payoff: discovery, not an echo of what they liked.
    // Builds a taste profile from likes (categories + vibe + wiggle + price)
    // and scores the unseen pool against it. Falls back to most-loved
    // products when the shopper liked nothing.

    function getRecommendations(limit) {
      limit = limit || MAX_RECOMMENDATIONS;

      // Build a taste profile from the liked products
      var tagWeights = {};
      var vibeWeights = {};
      var wiggleSum = 0, wiggleN = 0;
      var priceSum = 0, priceN = 0;
      liked.forEach(function (p) {
        (p.tags || []).forEach(function (t) {
          t = String(t).toLowerCase();
          tagWeights[t] = (tagWeights[t] || 0) + 1;
        });
        if (p.vibe) vibeWeights[p.vibe] = (vibeWeights[p.vibe] || 0) + 1;
        var w = parseInt(p.wiggle_level, 10);
        if (!isNaN(w)) { wiggleSum += w; wiggleN++; }
        if (p.price_cents) { priceSum += p.price_cents; priceN++; }
      });
      var avgWiggle  = wiggleN ? wiggleSum / wiggleN : null;
      var avgPrice   = priceN ? priceSum / priceN : null;
      var hasProfile = Object.keys(tagWeights).length > 0;

      function popularity(p) {
        var r  = parseFloat(p.rating) || 0;
        var rc = parseInt(p.review_count, 10) || 0;
        return r * 2 + Math.min(Math.log(rc + 1), 3) * 0.5;
      }

      // Does this product share a theme with what the shopper liked?
      // This is the GATE — a recommendation must overlap on category or vibe,
      // otherwise "based on what you loved" is a lie.
      function sharesTheme(p) {
        var hit = (p.tags || []).some(function (t) { return tagWeights[String(t).toLowerCase()]; });
        return hit || !!(p.vibe && vibeWeights[p.vibe]);
      }

      function affinity(p) {
        var score = 0;
        // Category overlap — the dominant signal
        (p.tags || []).forEach(function (t) {
          t = String(t).toLowerCase();
          if (tagWeights[t]) score += tagWeights[t] * 3;
        });
        // Vibe match
        if (p.vibe && vibeWeights[p.vibe]) score += vibeWeights[p.vibe] * 1.5;
        // Wiggle intensity proximity
        var w = parseInt(p.wiggle_level, 10);
        if (avgWiggle !== null && !isNaN(w) && Math.abs(w - avgWiggle) <= 1) score += 1;
        // Price proximity (within ~40% of average liked price)
        if (avgPrice && p.price_cents && Math.abs(p.price_cents - avgPrice) <= avgPrice * 0.4) score += 0.75;
        // Gentle social-proof tiebreaker — refines ranking, never gates
        score += popularity(p) * 0.15;
        return score;
      }

      // Candidate pool: in-stock products the shopper never swiped on.
      // With a taste profile, keep only thematically matching products;
      // with no profile (liked nothing), fall back to most-loved overall.
      var scored = products
        .filter(function (p) {
          if (seenIds[p.id] || !p.available) return false;
          if (budgetCents && (!p.price_cents || p.price_cents > budgetCents)) return false;
          return hasProfile ? sharesTheme(p) : true;
        })
        .map(function (p) { return { p: p, score: hasProfile ? affinity(p) : popularity(p) }; });

      scored.sort(function (a, b) { return b.score - a.score; });
      return scored.slice(0, limit).map(function (x) { return x.p; });
    }

    // ── Render a single result grid card ──────────────────────────────────

    function renderMatchCard(product) {
      var availableAttr = product.available ? '' : ' disabled';
      var btnLabel      = product.available ? 'Add to Cart' : 'Sold Out';
      var currency      = product.currency || '';
      var saleBadgeHTML = '';
      if (product.compare_price_cents && product.price_cents) {
        var discountPercentage = (product.compare_price_cents - product.price_cents) / product.compare_price_cents * 100;
        var pct = Math.round(discountPercentage);
        if (discountPercentage >= 5) saleBadgeHTML = '<span class="product-sale-badge badge">' + pct + '% Off</span>';
      }
      if (!saleBadgeHTML && product.shopify_tags && product.shopify_tags.map(function (t) { return t.toLowerCase(); }).indexOf('new') > -1) {
        saleBadgeHTML = '<span class="product-new-badge badge"> New! </span>';
      }
      var hasRange = product.price_max_cents && product.price_max_cents !== product.price_cents;
      var priceHTML;
      if (hasRange) {
        priceHTML = '<span class="product-card-price-final"><span class="product-card-price-from-label">From </span>' + escapeHTML(product.price) + '&nbsp;<span class="price-currency">' + escapeHTML(currency) + '</span></span>';
      } else if (product.compare_price) {
        priceHTML = '<s class="product-card-price-compare text-muted ms-2">' + escapeHTML(product.compare_price) + '</s><span class="product-card-price-final product-price-final-sale">' + escapeHTML(product.price) + '&nbsp;<span class="price-currency">' + escapeHTML(currency) + '</span></span>';
      } else {
        priceHTML = '<span class="product-card-price-final">' + escapeHTML(product.price) + '&nbsp;<span class="price-currency">' + escapeHTML(currency) + '</span></span>';
      }

      return (
        '<div class="product-card">' +
          '<div class="product-card-link">' +
            '<div class="product-card-visual position-relative">' +
              '<a href="' + escapeAttr(product.url) + '" class="text-decoration-none">' +
                '<div class="product-card-img-wrapper position-relative" style="aspect-ratio:1/1;">' +
                  '<img src="' + escapeAttr(product.image) + '" alt="' + escapeAttr(product.image_alt) + '" class="product-card-image w-100 h-100 object-fit-cover" loading="lazy">' +
                  saleBadgeHTML +
                '</div>' +
              '</a>' +
            '</div>' +
            '<div class="px-2">' +
              '<div class="product-card-body mt-3">' +
                '<h3 class="product-card-title h5 mb-2">' +
                  '<a href="' + escapeAttr(product.url) + '" class="text-decoration-none">' + escapeHTML(product.title) + '</a>' +
                '</h3>' +
                '<p class="product-card-price product-price mb-4">' + priceHTML + '</p>' +
              '</div>' +
            '</div>' +
          '</div>' +
          '<div class="product-card-buttons ff-result-card-buttons">' +
            '<div class="ff-atc-row">' +
              '<button class="btn btn-primary btn-atc ff-atc-btn"' +
                ' data-variant-id="' + escapeAttr(String(product.variant_id || '')) + '"' +
                availableAttr + '>' +
                btnLabel +
              '</button>' +
              '<button class="ff-wishlist-btn" data-product-handle="' + escapeAttr(product.handle || '') + '" type="button" aria-label="Add to wishlist">' +
                '<svg class="wishlist-heart" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">' +
                  '<path class="wishlist-heart-outline" d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>' +
                '</svg>' +
              '</button>' +
            '</div>' +
            '<a href="' + escapeAttr(product.url) + '" class="btn btn-outline-primary btn-atc">View →</a>' +
          '</div>' +
        '</div>'
      );
    }

    // ── Wire ATC + wishlist buttons inside a rendered result grid ──────────
    // `list` is the product array the grid was rendered from (variant lookup).

    function wireResultCardButtons(gridEl, list, isTopMatch) {
      if (!gridEl) return;
      gridEl.querySelectorAll('.ff-atc-btn').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var vid = parseInt(btn.dataset.variantId, 10);
          if (isNaN(vid)) return;
          var matched = list.find(function (p) { return String(p.variant_id) === String(vid); });
          trackEvent('QuizATC', {
            product_id:        matched ? String(matched.id) : '',
            product_title:     matched ? matched.title : '',
            product_price:     matched ? matched.price : '',
            is_top_match:      !!isTopMatch,
            personality_label: currentPersonality ? currentPersonality.label : ''
          });
          addToCart([{ id: vid, quantity: 1 }], btn);
        });
      });
      if (window.WishlistStore) {
        gridEl.querySelectorAll('.ff-wishlist-btn').forEach(function (btn) {
          var handle = btn.getAttribute('data-product-handle');
          if (!handle) return;
          btn.classList.toggle('is-wishlisted', window.WishlistStore.has(handle));
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            e.stopPropagation();
            var added = window.WishlistStore.toggle(handle);
            btn.classList.toggle('is-wishlisted', added);
            btn.setAttribute('aria-label', added ? 'Remove from wishlist' : 'Add to wishlist');
          });
        });
      }
    }

    // Render the "products you haven't seen" recommendation grid.
    function renderRecommendations(personality) {
      if (!recsEl || !recsGrid) return 0;
      var recs = getRecommendations(MAX_RECOMMENDATIONS);
      if (!recs.length) {
        recsEl.classList.add('d-none');
        recsGrid.innerHTML = '';
        return 0;
      }
      var isGift = mode === 'gift';
      if (liked.length) {
        if (recsTitleEl) recsTitleEl.textContent = isGift ? "They'll also love these" : "Fresh picks you haven't seen";
        if (recsSubEl)   recsSubEl.textContent   = isGift
          ? 'More in their vibe' + (budgetCents ? ' and budget.' : '.')
          : 'Based on what you loved — straight from the rest of the shelf.';
      } else {
        if (recsTitleEl) recsTitleEl.textContent = 'Most-loved wiggles';
        if (recsSubEl)   recsSubEl.textContent   = isGift
          ? 'Crowd favourites that make great gifts.'
          : "Not feeling those? Here's what other collectors adore.";
      }
      recsGrid.innerHTML = recs.map(renderMatchCard).join('');
      wireResultCardButtons(recsGrid, recs, false);
      recsEl.classList.remove('d-none');
      trackEvent('QuizRecommendations', {
        quiz_mode:         mode,
        rec_count:         recs.length,
        liked_count:       liked.length,
        personality_label: personality ? personality.label : ''
      });
      return recs.length;
    }

    // ── Results screen ────────────────────────────────────────────────────

    function showResults() {
      var personality = getPersonality();
      var topMatches  = getTopMatches(3);
      currentPersonality = personality;

      var isGift = mode === 'gift';

      trackEvent('PersonalityRevealed', {
        quiz_mode:         mode,
        personality_label: personality.label,
        liked_count:       liked.length,
        total_cards:       deck.length
      });

      // Personality reveal is a self-discovery payoff — it makes no sense
      // when you're buying for someone else, so hide it in gift mode.
      if (personalityEl) personalityEl.classList.toggle('d-none', isGift);
      if (personalityEmoji) personalityEmoji.textContent = personality.emoji;
      if (personalityTitle) personalityTitle.textContent = personality.label;
      if (personalityDesc)  personalityDesc.textContent  = personality.desc;

      // ── Top matches: spotlit as equal cards (a gift shortlist in gift mode) ──
      if (topMatchEl) {
        if (topMatches.length) {
          var topTitle = isGift
            ? (topMatches.length > 1 ? 'Your Gift Shortlist' : 'Your Gift Pick')
            : (topMatches.length > 1 ? 'Your Top Matches' : 'Your Top Match');
          topMatchEl.innerHTML =
            '<div class="ff-results-header">' +
              '<h3 class="ff-results-title">' + topTitle + '</h3>' +
            '</div>' +
            '<div class="ff-results-grid">' + topMatches.map(renderMatchCard).join('') + '</div>';
          topMatchEl.classList.remove('d-none');
          wireResultCardButtons(topMatchEl, topMatches, true);
        } else {
          topMatchEl.innerHTML = '';
          topMatchEl.classList.add('d-none');
        }
      }

      // ── Discovery: surface products the shopper never saw ──
      var recCount = renderRecommendations(personality);

      // ── More you liked (everything beyond the top matches) ──
      var topIds = {};
      topMatches.forEach(function (p) { topIds[p.id] = true; });
      var otherLikes = liked.filter(function (p) { return !topIds[p.id]; });

      if (otherLikes.length > 0) {
        if (resultsHeader) {
          resultsHeader.classList.remove('d-none');
          var moreTitle = resultsHeader.querySelector('.ff-results-title');
          if (moreTitle) moreTitle.textContent = isGift ? 'More they might like' : 'More you liked';
        }
        if (resultsGrid) {
          resultsGrid.innerHTML = otherLikes.map(renderMatchCard).join('');
          wireResultCardButtons(resultsGrid, liked, false);
        }
      } else {
        if (resultsHeader) resultsHeader.classList.add('d-none');
        if (resultsGrid)   resultsGrid.innerHTML = '';
      }

      // ── No-likes fallback: only when there's genuinely nothing to show ──
      if (noLikesEl) {
        if (!liked.length && recCount === 0) noLikesEl.classList.remove('d-none');
        else noLikesEl.classList.add('d-none');
      }

      showScreen(resultsEl);
    }

    // ── Cart: add items ───────────────────────────────────────────────────

    function addToCart(items, triggerBtn) {
      if (triggerBtn) {
        triggerBtn.disabled    = true;
        triggerBtn.textContent = 'Adding…';
      }

      // Tag each item so the order shows it came from the Fidget Finder.
      // Hidden '_'-prefixed property: kept on the order, hidden in cart/checkout UI.
      items = items.map(function (it) {
        return Object.assign({}, it, {
          properties: Object.assign({ _source: 'Fidget Finder' }, it.properties)
        });
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          return res.json().then(function (data) {
            if (!res.ok) {
              throw new Error(data.description || data.message || 'Cart error');
            }
            return data;
          });
        })
        .then(function () {
          if (triggerBtn) {
            triggerBtn.textContent = 'Added ✓';
            triggerBtn.classList.add('ff-btn--added');
          }
          refreshCartUI();
        })
        .catch(function () {
          if (triggerBtn) {
            triggerBtn.disabled    = false;
            triggerBtn.textContent = 'Try again';
          }
        });
    }

    function refreshCartUI() {
      fetch('/?sections=cart-count-badge,offcanvas-cart')
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          // Update cart count badge
          if (sections['cart-count-badge']) {
            document.querySelectorAll('.cart-count-badge').forEach(function (el) {
              var tmp = document.createElement('div');
              tmp.innerHTML = sections['cart-count-badge'];
              var fresh = tmp.querySelector('.cart-count-badge');
              if (fresh) {
                el.innerHTML   = fresh.innerHTML;
                el.className   = fresh.className;
              }
            });
          }

          // Update & open cart drawer
          var drawer = document.getElementById('offcanvas-cart');
          if (drawer && sections['offcanvas-cart']) {
            var tmp = document.createElement('div');
            tmp.innerHTML = sections['offcanvas-cart'];
            var freshDrawer = tmp.querySelector('#offcanvas-cart');
            if (freshDrawer) drawer.innerHTML = freshDrawer.innerHTML;
          }

          if (drawer && window.bootstrap) {
            window.bootstrap.Offcanvas.getOrCreateInstance(drawer).show();
          }
        })
        .catch(function () {});
    }

    // ── Share ─────────────────────────────────────────────────────────────

    function shareResult() {
      var personality = getPersonality();
      var text = 'I\'m "' + personality.label + '" ' + personality.emoji + ' — find your fidget match!';
      var url  = window.location.href;
      var orig = shareBtn ? shareBtn.textContent : '';

      var shareText = text + '\n' + url;
      if (navigator.share) {
        navigator.share({ title: 'My Fidget Personality', text: text, url: url }).catch(function () {});
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(shareText)
          .then(function () {
            if (shareBtn) {
              shareBtn.textContent = 'Copied ✓';
              setTimeout(function () { shareBtn.textContent = orig; }, 2200);
            }
          })
          .catch(function () {
            window.prompt('Copy your result:', shareText);
          });
      } else {
        window.prompt('Copy your result:', shareText);
      }
    }

    // ── Keyboard navigation ───────────────────────────────────────────────

    function onKeyDown(e) {
      if (!swipeEl || swipeEl.classList.contains('d-none')) return;
      if (isAnimating) return;
      var card;
      if (e.key === 'ArrowRight') {
        card = getTopCard();
        if (card) animateFlyOut(card, true);
      } else if (e.key === 'ArrowLeft') {
        card = getTopCard();
        if (card) animateFlyOut(card, false);
      }
    }

    document.addEventListener('keydown', onKeyDown);

    // ── Restart ───────────────────────────────────────────────────────────

    function startQuiz(opts) {
      opts = opts || {};
      mode = opts.mode === 'gift' ? 'gift' : 'self';
      budgetCents = opts.budgetCents || null;

      var pool = products.slice();
      if (budgetCents) {
        var inBudget = pool.filter(function (p) { return p.price_cents && p.price_cents <= budgetCents; });
        // Don't strand the shopper with an empty/tiny deck if the budget is tight.
        if (inBudget.length >= 4) pool = inBudget;
        else budgetCents = null;
      }

      // Cap the deck so the quiz stays short — the rest of the catalogue
      // becomes the pool we recommend from at the end.
      deck = shuffle(pool).slice(0, MAX_DECK_SIZE);
      currentIndex = 0;
      liked        = [];
      seenIds      = {};
      isAnimating  = false;
      if (earlyExitBtn) earlyExitBtn.classList.add('d-none');
      trackEvent('QuizStarted', {
        quiz_mode:   mode,
        quiz_budget: budgetCents ? (budgetCents / 100) : 'any',
        deck_size:   deck.length
      });
      renderStack();
      updateProgress();
      showScreen(swipeEl);
    }

    function restart() {
      mode         = 'self';
      budgetCents  = null;
      deck         = shuffle(products.slice()).slice(0, MAX_DECK_SIZE);
      currentIndex = 0;
      liked        = [];
      seenIds      = {};
      isAnimating  = false;
      if (earlyExitBtn) earlyExitBtn.classList.add('d-none');
      if (giftBudgetEl) giftBudgetEl.classList.add('d-none');
      if (giftBtn)      giftBtn.classList.remove('d-none');
      cleanupDocListeners();
      showScreen(introEl);
    }

    // ── Button event listeners ────────────────────────────────────────────

    if (startBtn) {
      startBtn.addEventListener('click', function () {
        startQuiz({ mode: 'self' });
      });
    }

    // Gift entry reveals the budget chips; each chip starts a gift-mode quiz.
    if (giftBtn) {
      giftBtn.addEventListener('click', function () {
        giftBtn.classList.add('d-none');
        if (giftBudgetEl) giftBudgetEl.classList.remove('d-none');
      });
    }

    if (giftBudgetEl) {
      giftBudgetEl.addEventListener('click', function (e) {
        var chip = e.target.closest('.ff-budget-chip');
        if (!chip) return;
        var b = parseInt(chip.dataset.budget, 10);
        startQuiz({ mode: 'gift', budgetCents: isNaN(b) ? null : b });
      });
    }

    if (nopeBtn) {
      nopeBtn.addEventListener('click', function () {
        if (isAnimating) return;
        var card = getTopCard();
        if (card) animateFlyOut(card, false);
      });
    }

    if (likeBtn) {
      likeBtn.addEventListener('click', function () {
        if (isAnimating) return;
        var card = getTopCard();
        if (card) animateFlyOut(card, true);
      });
    }

    if (earlyExitBtn) {
      earlyExitBtn.addEventListener('click', function () {
        if (isAnimating) return;
        earlyExitBtn.classList.add('d-none');
        completeProgress();
        showResults();
      });
    }

    if (shareBtn)   shareBtn.addEventListener('click', shareResult);
    if (restartBtn) restartBtn.addEventListener('click', restart);
  }

  // ── Analytics ─────────────────────────────────────────────────────────────

  function trackEvent(eventName, params) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: eventName }, params));
    } catch (e) {}
  }

  // ── Utilities ─────────────────────────────────────────────────────────────

  function shuffle(arr) {
    var a = arr.slice();
    for (var i = a.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = a[i]; a[i] = a[j]; a[j] = tmp;
    }
    return a;
  }

  function escapeHTML(str) {
    return String(str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    return String(str || '').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ── Boot ──────────────────────────────────────────────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Shopify theme editor support
  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[id^="fidget-finder-"]');
    if (section && section.dataset.sectionId) {
      delete initializedSections[section.dataset.sectionId];
      initFinder(section.dataset.sectionId);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector('[id^="fidget-finder-"]');
    if (section && section.dataset.sectionId) {
      delete initializedSections[section.dataset.sectionId];
    }
  });

})();
